import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Filter, Edit, Trash2, AlertTriangle, Shield } from 'lucide-react';
import { riskService } from '../../services/riskService';
import { CRITICALITY_LEVELS } from '../../types/risk';
import RiskEditor from './RiskEditor';
import './RisksList.css';

const RISK_LEVEL = { STANDARD: 'standard', PROTOCOL: 'protocol' };

const RisksList = ({ organizationId, userId, userRole = 'administration' }) => {
  const { t } = useTranslation('risks');
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');
  const [filterLevel, setFilterLevel] = useState(''); // '' = all, 'standard', 'protocol'
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch all risks
  const fetchRisks = async () => {
    try {
      setLoading(true);
      const data = await riskService.getAllRisks();
      setRisks(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching risks:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRisks();
  }, []);

  // Compute risk level for display (QMS Phase 2: standard vs protocol)
  const getRiskLevel = (risk) => (risk.processId ? RISK_LEVEL.STANDARD : RISK_LEVEL.PROTOCOL);

  // Filter risks
  const filteredRisks = risks.filter(risk => {
    const matchesSearch = !searchQuery ||
      risk.riskScenario?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      risk.riskDescription?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      risk.riskType?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCriticality = !filterCriticality || risk.criticality === filterCriticality;

    const level = getRiskLevel(risk);
    const matchesLevel = !filterLevel || level === filterLevel;

    return matchesSearch && matchesCriticality && matchesLevel;
  });

  // Group risks by criticality
  const groupedRisks = {
    [CRITICALITY_LEVELS.CRITICAL]: filteredRisks.filter(r => r.criticality === CRITICALITY_LEVELS.CRITICAL),
    [CRITICALITY_LEVELS.HIGH]: filteredRisks.filter(r => r.criticality === CRITICALITY_LEVELS.HIGH),
    [CRITICALITY_LEVELS.MEDIUM]: filteredRisks.filter(r => r.criticality === CRITICALITY_LEVELS.MEDIUM),
    [CRITICALITY_LEVELS.LOW]: filteredRisks.filter(r => r.criticality === CRITICALITY_LEVELS.LOW)
  };

  const handleCreateRisk = () => {
    setSelectedRisk(null);
    setShowEditor(true);
  };

  const handleEditRisk = (risk) => {
    setSelectedRisk(risk);
    setShowEditor(true);
  };

  const handleDeleteRisk = (risk) => {
    setDeleteConfirm(risk);
  };

  const confirmDeleteRisk = async () => {
    if (!deleteConfirm) return;
    setDeleteError(null);
    try {
      await riskService.deleteRisk(deleteConfirm.id);
      fetchRisks();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting risk:', error);
      setDeleteError(t('failedToDeleteRisk'));
    }
  };

  const handleSaveRisk = () => {
    setShowEditor(false);
    setSelectedRisk(null);
    fetchRisks();
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedRisk(null);
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality) {
      case CRITICALITY_LEVELS.CRITICAL: return 'critical';
      case CRITICALITY_LEVELS.HIGH: return 'high';
      case CRITICALITY_LEVELS.MEDIUM: return 'medium';
      case CRITICALITY_LEVELS.LOW: return 'low';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="risks-list-loading">
        <div className="loading-spinner"></div>
        <p>{t('loadingRisks')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="risks-list-error">
        <AlertTriangle className="error-icon" />
        <p>{t('errorLoading', { message: error.message })}</p>
        <button onClick={fetchRisks} className="btn btn-secondary">
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="risks-list-container">
      {/* Header */}
      <div className="risks-list-header">
        <div>
          <h1>{t('riskManagement')}</h1>
          <p className="subtitle">{t('riskManagementSubtitle')}</p>
        </div>
        <button onClick={handleCreateRisk} className="btn btn-primary">
          <Plus size={16} />
          {t('createRisk')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="risk-summary-cards">
        <div className="summary-card total">
          <div className="summary-icon">
            <Shield size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-number">{risks.length}</div>
            <div className="summary-label">{t('totalRisks')}</div>
          </div>
        </div>
        <div className="summary-card critical-card">
          <div className="summary-icon critical">
            <AlertTriangle size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-number">{groupedRisks[CRITICALITY_LEVELS.CRITICAL].length}</div>
            <div className="summary-label">{t('criticalRpn200')}</div>
          </div>
        </div>
        <div className="summary-card high-card">
          <div className="summary-icon high">
            <AlertTriangle size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-number">{groupedRisks[CRITICALITY_LEVELS.HIGH].length}</div>
            <div className="summary-label">{t('highRpn101200')}</div>
          </div>
        </div>
        <div className="summary-card medium-card">
          <div className="summary-icon medium">
            <AlertTriangle size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-number">{groupedRisks[CRITICALITY_LEVELS.MEDIUM].length}</div>
            <div className="summary-label">{t('mediumRpn51100')}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="risks-list-filters">
        <div className="search-bar">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder={t('searchRisks')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <Filter size={16} className="filter-icon" />
          <select
            value={filterCriticality}
            onChange={(e) => setFilterCriticality(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('allCriticalityLevels')}</option>
            <option value={CRITICALITY_LEVELS.CRITICAL}>{t('critical')}</option>
            <option value={CRITICALITY_LEVELS.HIGH}>{t('high')}</option>
            <option value={CRITICALITY_LEVELS.MEDIUM}>{t('medium')}</option>
            <option value={CRITICALITY_LEVELS.LOW}>{t('low')}</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('allLevels')}</option>
            <option value={RISK_LEVEL.STANDARD}>{t('standard')}</option>
            <option value={RISK_LEVEL.PROTOCOL}>{t('protocol')}</option>
          </select>
        </div>
      </div>

      {/* Risks Table */}
      {filteredRisks.length === 0 ? (
        <div className="empty-state">
          <AlertTriangle className="empty-icon" />
          <h3>{t('noRisksFound')}</h3>
          <p>
            {risks.length === 0 
              ? t('noRisksYet')
              : t('noRisksMatchFilter')
            }
          </p>
          {risks.length === 0 && (
            <button onClick={handleCreateRisk} className="btn btn-primary">
              <Plus size={16} />
              {t('createFirstRisk')}
            </button>
          )}
        </div>
      ) : (
        <div className="risks-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('riskScenario')}</th>
                <th>{t('description')}</th>
                <th>{t('type')}</th>
                <th>{t('source')}</th>
                <th>{t('criticality')}</th>
                <th>{t('rpn')}</th>
                <th>{t('biologicalEntities')}</th>
                <th>{t('standard')}</th>
                <th>{t('protocol')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRisks.map(risk => (
                <tr key={risk.id}>
                  <td>
                    <div className="risk-scenario-cell">
                      <strong>{risk.riskScenario}</strong>
                      {risk.oScoreReviewPending === true && (
                        <span
                          className="risk-o-review-badge"
                          title={t('oScoreReviewTooltip', {
                            capaId: risk.oScoreReviewCapaId || 'N/A'
                          })}
                        >
                          ↻ {t('oScoreReviewBadge')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="risk-description-cell" title={risk.riskDescription}>
                      {risk.riskDescription ? 
                        (risk.riskDescription.length > 100 
                          ? risk.riskDescription.substring(0, 100) + '...' 
                          : risk.riskDescription) 
                        : t('noDescription')
                      }
                    </div>
                  </td>
                    <td>
                        <span className="type-badge">
                          {risk.riskType || t('unknown')}
                        </span>
                      </td>
                  <td>
                    <span className={`source-badge ${getRiskLevel(risk)}`}>
                      {getRiskLevel(risk) === RISK_LEVEL.STANDARD ? t('standard') : t('protocol')}
                    </span>
                  </td>
                  <td>
                    <span className={`criticality-badge ${getCriticalityColor(risk.criticality)}`}>
                      {risk.criticality}
                    </span>
                  </td>
                  <td>
                    <div className="rpn-cell">
                      <span className="rpn-value">{risk.rpn}</span>
                    </div>
                  </td>
                  <td>
                    <div className="biological-entities-cell">
                      {risk.biological_entities && risk.biological_entities.length > 0 ? (
                        <div>
                          <div className="entities-list">
                            {risk.biological_entities.slice(0, 2).map(entity => (
                              <span key={entity.biologicalEntityId} className="entity-badge">
                                {entity.biologicalEntityName}
                              </span>
                            ))}
                            {risk.biological_entities.length > 2 && (
                              <span className="entity-badge more">
                                +{risk.biological_entities.length - 2}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="no-entities">{t('none')}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="standard-association-cell">
                      {risk.processId ? (
                        <div
                          className="standard-code-only"
                          title={risk.processName || risk.processCode || risk.processId}
                        >
                          {risk.processCode || risk.processId}
                        </div>
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="protocol-association-cell">
                      {risk.protocolId && risk.protocolName ? (
                        <div>
                          <div className="protocol-name">{risk.protocolName}</div>
                          {risk.stepName && <div className="step-name">→ {risk.stepName}</div>}
                          {risk.actionName && <div className="action-name">→ {risk.actionName}</div>}
                        </div>
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-edit btn-icon"
                        onClick={() => handleEditRisk(risk)}
                        title={t('editRisk')}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="btn btn-delete btn-icon"
                        onClick={() => handleDeleteRisk(risk.id, risk.riskScenario)}
                        title={t('deleteRisk')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Risk Editor Modal */}
      {showEditor && (
        <RiskEditor
          risk={selectedRisk}
          protocol={null}
          level="standalone"
          organizationId={organizationId}
          userId={userId}
          userRole={userRole}
          onClose={handleCloseEditor}
          onSave={handleSaveRisk}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{t('confirmDeleteRiskTitle')}</h3>
            <p>{t('confirmDeleteRisk', { scenario: deleteConfirm.riskScenario || deleteConfirm.id })}</p>
            {deleteError && <p className="error-message">{deleteError}</p>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>{t('cancel')}</button>
              <button className="btn btn-danger" onClick={confirmDeleteRisk}>{t('delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RisksList;
